import { toast } from "@/components/common/Toast"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { IconLoader2 } from "@tabler/icons-react"
import { useState } from "react"
import { getApiErrorMessage } from "../../api/apiError"
import { useConnectErp, type ErpSystemId } from "../../api/importsApi"

type AuthType = "apikey" | "basic" | "oauth"

const AUTH_TYPE_LABEL: Record<AuthType, TranslationKeys> = {
  apikey: "MC_MD_ERP_CRED_AUTH_APIKEY",
  basic: "MC_MD_ERP_CRED_AUTH_BASIC",
  oauth: "MC_MD_ERP_CRED_AUTH_OAUTH",
}

// Fields validated as http(s) URLs (mirrors the backend's validate_base_url).
const URL_FIELDS = new Set(["base_url", "token_url"])

function isHttpUrl(v: string): boolean {
  try {
    const u = new URL(v.trim())
    return u.protocol === "http:" || u.protocol === "https:"
  } catch {
    return false
  }
}

/** Secret fields render as password inputs; the rest as text. */
const FIELDS_BY_AUTH: Record<
  AuthType,
  { key: string; labelKey: TranslationKeys; secret?: boolean }[]
> = {
  apikey: [
    { key: "api_key", labelKey: "MC_MD_ERP_CRED_API_KEY", secret: true },
  ],
  basic: [
    { key: "username", labelKey: "MC_MD_ERP_CRED_USERNAME" },
    { key: "password", labelKey: "MC_MD_ERP_CRED_PASSWORD", secret: true },
  ],
  oauth: [
    { key: "token_url", labelKey: "MC_MD_ERP_CRED_TOKEN_URL" },
    { key: "client_id", labelKey: "MC_MD_ERP_CRED_CLIENT_ID" },
    {
      key: "client_secret",
      labelKey: "MC_MD_ERP_CRED_CLIENT_SECRET",
      secret: true,
    },
  ],
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  erpType: ErpSystemId
  erpName: string
  /** Auth choices this ERP accepts (from the catalog); the Select shows only these. */
  authTypes: AuthType[]
}

/**
 * Connect modal for credentials-form ERPs (SAP, on-prem). Collects a base URL +
 * auth method and its fields, then stores the (server-side encrypted) connection.
 * Microsoft-OAuth ERPs use the sign-in flow instead and never reach here.
 */
export function ErpCredentialsDialog({
  open,
  onOpenChange,
  erpType,
  erpName,
  authTypes,
}: Props) {
  const { translate } = useTranslation()
  const { mutate: connectErp, isPending } = useConnectErp()
  const [baseUrl, setBaseUrl] = useState("")
  const [authType, setAuthType] = useState<AuthType>(authTypes[0] ?? "apikey")
  const [fields, setFields] = useState<Record<string, string>>({})
  const [attempted, setAttempted] = useState(false)

  const authFields = FIELDS_BY_AUTH[authType]
  const valueOf = (key: string) =>
    key === "base_url" ? baseUrl : (fields[key] ?? "")

  // Per-field error. URL-format errors show live; "required" only after a submit
  // attempt, so the form isn't red before the user has typed anything.
  function errorFor(key: string): string {
    const v = valueOf(key).trim()
    if (!v) return attempted ? translate("MC_MD_ERP_CRED_ERR_REQUIRED") : ""
    if (URL_FIELDS.has(key) && !isHttpUrl(v))
      return translate("MC_MD_ERP_CRED_ERR_URL")
    return ""
  }

  function handleConnect() {
    setAttempted(true)
    const keys = ["base_url", ...authFields.map((f) => f.key)]
    const invalid = keys.some((k) => {
      const v = valueOf(k).trim()
      return !v || (URL_FIELDS.has(k) && !isHttpUrl(v))
    })
    if (invalid) return

    const credentials: Record<string, string> = {
      base_url: baseUrl.trim(),
      auth_type: authType,
    }
    for (const f of authFields)
      credentials[f.key] = (fields[f.key] ?? "").trim()

    connectErp(
      { erpType, body: { credentials } },
      {
        onSuccess: () => onOpenChange(false),
        onError: (e) =>
          toast.error(
            getApiErrorMessage(e) ?? translate("MC_MD_ERP_CONNECT_ERROR"),
          ),
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md gap-4">
        <DialogHeader>
          <DialogTitle>
            {translate("MC_MD_ERP_CRED_TITLE", { name: erpName })}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <Field
            label={translate("MC_MD_ERP_CRED_BASE_URL")}
            required
            error={errorFor("base_url")}
          >
            <Input
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://my.s4hana.example.com/sap/opu/odata/sap"
            />
          </Field>

          <Field label={translate("MC_MD_ERP_CRED_AUTH_TYPE")} required>
            <Select
              value={authType}
              onValueChange={(v) => {
                setAuthType(v as AuthType)
                setFields({})
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {authTypes.map((t) => (
                  <SelectItem key={t} value={t}>
                    {translate(AUTH_TYPE_LABEL[t])}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          {authFields.map((f) => (
            <Field
              key={f.key}
              label={translate(f.labelKey)}
              required
              error={errorFor(f.key)}
            >
              <Input
                type={f.secret ? "password" : "text"}
                autoComplete={f.secret ? "new-password" : "off"}
                value={fields[f.key] ?? ""}
                onChange={(e) =>
                  setFields((prev) => ({ ...prev, [f.key]: e.target.value }))
                }
              />
            </Field>
          ))}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {translate("MC_MD_ERP_WIZARD_CANCEL")}
          </Button>
          <Button onClick={handleConnect} disabled={isPending}>
            {isPending && <IconLoader2 className="animate-spin" size={14} />}
            {translate("MC_MD_ERP_CRED_CONNECT")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string
  required?: boolean
  error?: string
  children: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-xs text-grey5">
        {label}
        {required && <span className="ml-0.5 text-red1">*</span>}
      </Label>
      {children}
      {error && <span className="text-[11px] text-red1">{error}</span>}
    </div>
  )
}
